/// <amd-module name="Wit/WorkItemForm/Details/WorkItemFormTab" />
import { FormPage, PageLayoutMode } from "Wit/Clients/Wit/Contracts/WorkItemFormLayout";
import { IWorkItemNotificationListener } from "Wit/WorkItemForm/Contracts/Contracts";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import * as React from "react";
import { useEffect, useState } from "react";
import { WorkItemFormDiscussionGroup } from "./WorkItemFormDiscussionGroup";
import { WorkItemFormExtensionHost } from "./WorkItemFormExtensionHost";
import { WorkItemFormSection } from "./WorkItemFormSection";
import { css } from "VSSUI/Util";


interface IWorkItemFormTabProps {
    gridLayoutEnabled?: boolean;
    includeDiscussion: boolean;
    onExtensionLoaded: (workItem: IWorkItem, hostInstance: IWorkItemNotificationListener) => void;
    page: FormPage;
}

export function WorkItemFormTab({ gridLayoutEnabled, includeDiscussion, onExtensionLoaded, page }: IWorkItemFormTabProps) {
    const filteredSections = page.sections.filter(s => s.groups.length > 0 && s.groups.some(g => g.visible));

    const firstColumnWide = page.layoutMode === PageLayoutMode.firstColumnWide;
    const sectionCountMax = filteredSections.length + (firstColumnWide ? 1 : 0);
    const firstSectionWidthCount = firstColumnWide ? 2 : 1;

    const [sectionsPerRow, setSectionsPerRow] = useState(sectionCountMax);

    useEffect(() => {
        window.addEventListener("resize", onResize);
        onResize();

        return () => window.removeEventListener("resize", onResize);
    });

    const onResize = () => {
        const tab = document.querySelector(".work-item-form-tab");
        if (tab) {
            // Determine how many sections can fit in the tab then takes the min between that and the number of sections we have.
            setSectionsPerRow(Math.min(Math.floor(tab.clientWidth / 256), sectionCountMax));
        }
    };

    if (page.isContribution) {
        return <WorkItemFormExtensionHost contributionId={page.contribution.contributionId} onLoaded={onExtensionLoaded} />;
    }

    if (includeDiscussion) {
        const firstSection = filteredSections[0];

        // Feature Flag: WebPlatform.WorkItemTracking.NewBoardsWorkItemGridLayout
        if (gridLayoutEnabled) {
            return (
                <div className={css("work-item-grid-ff-enabled work-item-form-tab full-width", firstColumnWide ? "first-column-wide" : "", `right-column-count-${filteredSections?.length - 1}`, `section-count-${sectionsPerRow}`)}>
                    <div className="work-item-form-first-section">
                        <WorkItemFormSection
                            gridLayoutEnabled={true}
                            onExtensionLoaded={onExtensionLoaded}
                            key={firstSection.id + "-form-section"}
                            keyPrefix={firstSection.id}
                            section={firstSection}
                        />
                    </div>
                    <div className="work-item-form-discussion">
                        <WorkItemFormDiscussionGroup gridLayoutEnabled={true} />
                    </div>
                    <div className="work-item-form-right other-form-sections flex-row flex-wrap">
                        {filteredSections.map(
                            (s, index) =>
                                index !== 0 && (
                                    <WorkItemFormSection
                                        gridLayoutEnabled={true}
                                        onExtensionLoaded={onExtensionLoaded}
                                        key={s.id + "-form-section"}
                                        keyPrefix={s.id}
                                        section={s}
                                    />
                                )
                        )}
                    </div>
                </div>
            );
        } else {
            // What percent of space is left after the first section.
            const otherSectionsWidth = ((sectionsPerRow - firstSectionWidthCount) / sectionsPerRow) * 100;

            return (
                <div className="work-item-form-tab full-width">
                    <WorkItemFormSection
                        onExtensionLoaded={onExtensionLoaded}
                        key={firstSection.id + "-form-section"}
                        keyPrefix={firstSection.id}
                        section={firstSection}
                        wide={firstColumnWide}
                        width={(100 / sectionsPerRow) * firstSectionWidthCount}
                    />
                    <div className="other-form-sections" style={{ width: `${otherSectionsWidth || 100}%` }}>
                        {filteredSections.map(
                            (s, index) =>
                                index !== 0 && (
                                    <WorkItemFormSection
                                        onExtensionLoaded={onExtensionLoaded}
                                        key={s.id + "-form-section"}
                                        keyPrefix={s.id}
                                        section={s}
                                        wide={false}
                                        width={100 / (sectionsPerRow - firstSectionWidthCount)}
                                    />
                                )
                        )}
                    </div>
                    <WorkItemFormDiscussionGroup wide={firstColumnWide} width={(100 / sectionsPerRow) * firstSectionWidthCount} />
                </div>
            );
        }
    }

    return (
        <div className="work-item-form-tab full-width">
            {filteredSections.map((s, index) => (
                <WorkItemFormSection
                    onExtensionLoaded={onExtensionLoaded}
                    key={s.id + "-form-section"}
                    keyPrefix={s.id}
                    section={s}
                    wide={firstColumnWide && index === 0}
                    width={(100 / sectionsPerRow) * (index === 0 ? firstSectionWidthCount : 1)}
                />
            ))}
        </div>
    );
}
